import {
  BlockTypeSelect,
  BoldItalicUnderlineToggles,
  CreateLink,
  diffSourcePlugin,
  DiffSourceToggleWrapper,
  headingsPlugin,
  InsertTable,
  linkDialogPlugin,
  listsPlugin,
  ListsToggle,
  MDXEditor,
  quotePlugin,
  Separator,
  tablePlugin,
  thematicBreakPlugin,
  toolbarPlugin,
  UndoRedo
} from '@mdxeditor/editor';
import '@mdxeditor/editor/style.css';
import { useEffect, useRef, useState } from 'react';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../api';
import { ButtonStatus, FileType } from '../common/constants';
import Breadcrumb from '../components/Breadcrumb';
import Button from "../components/Button";
import Card from '../components/cards/Card';
import HeaderFooterLayout from '../components/layouts/HeaderFooterLayout';
import MultipleSelect from "../components/MultipleSelect";
import TagSelector from "../components/TagSelector";
import TextInput from "../components/TextInput";
import Uploader from '../components/Uploader';
import styles from "./VideoEditor.module.css";

const VideoEditor = () => {
  const { video_id } = useParams()
  const navigate = useNavigate()
  const [video, setVideo] = useState(null)
  const [title, setTitle] = useState("")
  const [description, setDescription] = useState("")
  const [slug, setSlug] = useState("")
  const [tags, setTags] = useState([])
  const [filteredTags, setFilteredTags] = useState([])
  const [courses, setCourses] = useState([])
  const [selectedCourses, setSelectedCourses] = useState([])
  const [tab, setTab] = useState(0)

  const [thumbnail, setThumbnail] = useState(null)
  const [uploaderThumbnailReset, setUploaderThumbnailReset] = useState(null)

  const [url, setUrl] = useState("")
  const [uploaderVideoReset, setUploaderVideoReset] = useState(null)

  const [publishing, setPublishing] = useState(null)
  const mdRef = useRef(null)

  useEffect(() => {
    const getVideo = async () => {
      try {
        const video = await api.get(`/admin/videos/${video_id}`)
        setVideo(video)
      }
      catch (e) {
        console.error(e)
      }
    }

    if (video_id !== "create") {
      getVideo()
    }
  }, []);


  useEffect(() => {
    const getOptions = async () => {
      try {
        const [tags, courses] = await Promise.all([
          api.get("/admin/tags?type=general"),
          api.get("/admin/courses?complete=false"),
        ])
        setFilteredTags(tags)
        setCourses(Array.isArray(courses) ? courses : courses?.courses || [])
      }
      catch (e) {
        console.error(e)
      }
    }

    getOptions()
  }, [])

  useEffect(() => {
    if (video) {
      setTitle(video.title)
      setDescription(video.description)
      mdRef.current?.setMarkdown(video.description)
      setUrl(video.url)
      setThumbnail(video.thumbnail)
      setSlug(video.slug)
      setTags(video.tags || [])
      setSelectedCourses(video.courses || [])
    }

  }, [video])

  const saveVideo = async () => {
    try {
      setPublishing(ButtonStatus.Loading)
      const newVideo = {
        title,
        slug,
        description,
        url,
        thumbnail,
        tags: JSON.stringify(tags.map(t => t.id)),
        courses: selectedCourses.map(c => c.id),
      }
      const res = video_id === "create" ?
        await api.post("/admin/videos", newVideo) :
        await api.put(`/admin/videos/${video_id}`, newVideo)

      setPublishing(ButtonStatus.Success)
      setUploaderThumbnailReset(o => !o)
      setUploaderVideoReset(o => !o)
      if (video_id === "create") {
        navigate(`/videos/${res.id}`)
      }
    } catch (e) {
      setPublishing(ButtonStatus.Error)

      console.error(e)
    }
  }

  return (
    <HeaderFooterLayout>
      <HelmetProvider>
        <Helmet>
          <title>Editor video</title>
        </Helmet>
      </HelmetProvider>
      <div className={styles.container}>
        <div className={styles.section}>
          <div className={styles.sectionInner}>
            <Breadcrumb items={[{label: "Contenuti"}, {label: "Video", to: "/videos"}, {label: video_id === "create" ? "Nuovo video" : "Modifica video"}]} />
            <div className={styles.header}>
              <h1>
                {video_id === "create" ? "Aggiungi video" : "Modifica video"}
              </h1><p>Imposta contenuti, copertina e corsi a cui rendere disponibile il video.</p>
            </div>
            <Card>
              <div className={styles.content}>
                <div className={styles.top}>
                  <div className={styles.left}>
                    <div className={styles.labeled}>
                      <div className={styles.fieldLabel}>Titolo</div>
                      <TextInput appearance="modern"
                        type="text"
                        value={title}
                        placeholder={"Titolo"}
                        onKeyUp={(v) => {
                          setTitle(v)
                          const slug = v?.replace(/[^a-zA-Z0-9\s-]/g, '') // Rimuovi tutti i caratteri tranne lettere e numeri
                            .replace(/\s+/g, '-') // Sostituisci spazi con trattini
                            .toLowerCase()
                            .trim();
                          setSlug(slug)
                        }
                        }
                      />
                    </div>
                    <div className={styles.labeled}>
                      <div className={styles.fieldLabel}>Slug</div>
                      <TextInput appearance="modern"
                        type="text"
                        value={slug}
                        placeholder={"Slug"}
                        onKeyUp={(v) => setSlug(v)}
                      />
                    </div>
                    <div className={styles.labeled}>
                      <div className={styles.fieldLabel}>Tag</div>
                      <TagSelector
                        selected={tags}
                        options={filteredTags}
                        placeholder={(tags.length === 0 && "Seleziona un tag") || ""}

                        onAdd={(item) => {
                          setTags(t => {
                            t.push(item)
                            return [...t]
                          })
                        }}
                        onRemove={(item) => {
                          setTags(t => {
                            t = t.filter((tt) => tt.id !== item.id)
                            return [...t]
                          })
                        }}
                      />
                    </div>
                    <div className={styles.labeled}>
                      <div className={styles.fieldLabel}>Corsi associati</div>
                      <MultipleSelect appearance="modern" portal
                        selected={selectedCourses}
                        options={courses}
                        placeholder="Seleziona corsi"
                        allLabel="Tutti i corsi"
                        searchPlaceholder="Cerca corso..."
                        onSearch={() => { }}
                        onSelect={(items) => setSelectedCourses(items)}
                        style={{ width: "100%" }}
                      />
                    </div>
                    <div className={styles.labeled}>
                      <div className={styles.fieldLabel}>URL copertina</div>
                      <TextInput appearance="modern"
                        type="text"
                        value={thumbnail}
                        placeholder={"Url thumbnail"}
                        onKeyUp={(v) => setThumbnail(v)}
                      />
                    </div>
                    <div className={styles.labeled}>
                      <div className={styles.fieldLabel}>URL video</div>
                      <TextInput appearance="modern"
                        type="text"
                        value={url}
                        placeholder={"Url video"}
                        onKeyUp={(v) => setUrl(v)}
                      />
                    </div>
                  </div>
                  <div className={styles.right}>
                    <div className={styles.switch}>
                      <button type="button" aria-pressed={tab === 0} className={tab === 0 ? `${styles.tab}` : `${styles.tab} ${styles.notSelected}`}
                        onClick={() => setTab(0)}>Thumbnail</button>
                      <button type="button" aria-pressed={tab === 1} className={tab === 1 ? `${styles.tab}` : `${styles.tab} ${styles.notSelected}`}
                        onClick={() => setTab(1)}>Video</button>
                    </div>
                    <div className={styles.mediaContainer}>
                      {
                        tab === 0 &&
                        <Uploader
                          preview={thumbnail}
                          reset={uploaderThumbnailReset}
                          multiple={false}
                          type={FileType.Image}
                          onComplete={uploads => setThumbnail(uploads[0].url)}
                          onDelete={() => setThumbnail("")}
                        />
                      }
                      {
                        tab === 1 &&
                        <Uploader
                          preview={url}
                          reset={uploaderVideoReset}
                          multiple={false}
                          type={FileType.Video}
                          onComplete={uploads => setUrl(uploads[0].url)}
                          onDelete={() => setUrl("")}
                        />
                      }
                    </div>
                  </div>
                </div>
                <div className={styles.fieldLabel}>Descrizione</div><div className={styles.editor}>
                  <MDXEditor
                    ref={mdRef}
                    contentEditableClassName={styles.markdownEditor}
                    markdown={description}
                    placeholder="Descrizione"
                    onChange={(value) => { setDescription(value) }}
                    plugins={[
                      headingsPlugin(),
                      listsPlugin(),
                      quotePlugin(),
                      thematicBreakPlugin(),
                      linkDialogPlugin(),
                      diffSourcePlugin(),
                      tablePlugin(),
                      toolbarPlugin({
                        toolbarContents: () => (
                          <>
                            {' '}
                            <UndoRedo />
                            <Separator />
                            <BoldItalicUnderlineToggles />
                            <Separator />
                            <ListsToggle />
                            <CreateLink />
                            <InsertTable />
                            <Separator />
                            <BlockTypeSelect />
                            <DiffSourceToggleWrapper options={["rich-text", "source"]} />
                          </>
                        )
                      })
                    ]}
                  />
                </div>
                <div className={styles.action}>
                  <Button
                    status={publishing}
                    disabled={!title.trim() || !slug.trim() || !url.trim() || publishing === ButtonStatus.Loading}
                    style={{ padding: ".65rem 1rem", borderRadius: 8, fontSize: ".75rem" }}
                    onClick={saveVideo}
                  >
                    {video_id === "create" ? "Pubblica video" : "Salva modifiche"}
                  </Button>
                </div>
              </div>

            </Card>
          </div>
        </div>
      </div>
    </HeaderFooterLayout >
  )
}

export default VideoEditor
